import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { GripVertical, Trash2, Clock, Eye, Send, Loader2 } from 'lucide-react'
import { StepForm, type StepFormData } from './StepForm'
import { StepPreview } from './StepPreview'
import { MissingVariablesNotice } from './MissingVariablesNotice'
import { RenderContactSelect } from './RenderContactSelect'
import { useTestRecipientEmail } from '@/hooks/useTestRecipientEmail'
import { resolveStepSubject } from '@/lib/sequences'
import { collectMissingVariables } from '@/lib/templateVariables'
import { stripHtml } from '@/lib/utils'
import type { Contact, SequenceStep } from '@/types/api'

interface StepCardProps {
  step: SequenceStep
  stepNumber: number
  sequenceId: string
  signatureHtml?: string
  firstStepSubject?: string
  isEditing: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: (data: StepFormData) => void
  onDelete: () => void
}

const API_BASE = import.meta.env.VITE_API_URL || ''

export function StepCard({
  step,
  stepNumber,
  sequenceId,
  signatureHtml,
  firstStepSubject,
  isEditing,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}: StepCardProps) {
  const { t } = useTranslation(['sequences', 'common'])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [showTestDialog, setShowTestDialog] = useState(false)
  const [testEmail, setTestEmail] = useTestRecipientEmail()
  const [testSending, setTestSending] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const [testIsError, setTestIsError] = useState(false)
  // null = dati di esempio
  const [testContact, setTestContact] = useState<Contact | null>(null)
  const [testMissing, setTestMissing] = useState<string[]>([])
  // Oggetto (template) che riceve il destinatario: nei follow-up `Re:` del
  // primo step (#264). L'anteprima lo riceve già renderizzato dal server.
  const threadSubject = stepNumber > 1 ? firstStepSubject : undefined
  const sentSubject = resolveStepSubject(step.subject, threadSubject)

  const handleTestSend = async () => {
    if (!testEmail) return
    setTestSending(true)
    setTestResult(null)
    setTestIsError(false)
    setTestMissing([])
    try {
      const token = localStorage.getItem('access_token')
      const res = await fetch(`${API_BASE}/api/sequences/${sequenceId}/test-send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          to_email: testEmail,
          step_id: step.id,
          contact_id: testContact?.id ?? null,
          apply_enrollment_overrides: true,
        }),
      })
      const data = await res.json()
      if (res.ok) {
        setTestResult(t('step.testSent', { email: testEmail }))
        setTestMissing(collectMissingVariables(data.missing_variables))
      } else {
        setTestIsError(true)
        setTestResult(t('step.testError', { detail: data.detail || t('step.testSendFailed') }))
      }
    } catch {
      setTestIsError(true)
      setTestResult(t('step.testNetworkError'))
    } finally {
      setTestSending(false)
    }
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  if (isEditing) {
    return (
      <div ref={setNodeRef} style={style}>
        <StepForm
          initialData={{
            subject: step.subject,
            body_html: step.body_html,
            delay_days: step.delay_days,
            delay_hours: step.delay_hours,
          }}
          stepNumber={stepNumber}
          stepId={step.id}
          firstStepSubject={firstStepSubject}
          onSave={onSave}
          onCancel={onCancel}
        />
      </div>
    )
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={`border rounded-lg p-4 hover:bg-muted/50 transition-colors ${isDragging ? 'opacity-50' : ''}`}
      >
        <div className="flex items-start gap-4">
          <button
            type="button"
            className="text-muted-foreground cursor-grab active:cursor-grabbing mt-0.5"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="font-medium">{t('step.heading', { number: stepNumber })}</span>
                {(step.delay_days > 0 || step.delay_hours > 0) && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {step.delay_days > 0 && `${step.delay_days}d`}
                    {step.delay_hours > 0 && ` ${step.delay_hours}h`}
                    {' '}{t('step.delay')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setShowPreview(true)}>
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('step.sendTest')}
                  onClick={() => { setShowTestDialog(true); setTestResult(null); setTestIsError(false) }}
                >
                  <Send className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  {t('common:edit')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowDeleteDialog(true)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
            <p className="font-medium text-sm mb-1">{sentSubject}</p>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {stripHtml(step.body_html)}
            </p>
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('step.deleteTitle', { number: stepNumber })}</DialogTitle>
            <DialogDescription>
              {t('step.deleteDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('common:cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowDeleteDialog(false)
                onDelete()
              }}
            >
              {t('common:delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <StepPreview
        open={showPreview}
        onOpenChange={setShowPreview}
        sequenceId={sequenceId}
        stepId={step.id}
        signatureHtml={signatureHtml}
      />

      {/* Test send dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('step.sendTest')}</DialogTitle>
            <DialogDescription>
              {t('step.sendTestDescription', { number: stepNumber })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <RenderContactSelect value={testContact} onChange={setTestContact} />
            <Input
              type="email"
              placeholder={t('step.testEmailPlaceholder')}
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTestSend()}
            />
            <p className="text-xs text-muted-foreground">{t('step.sendTestRecipientHint')}</p>
            {testResult && (
              <p className={`text-sm ${testIsError ? 'text-red-500' : 'text-green-600'}`}>
                {testResult}
              </p>
            )}
            <MissingVariablesNotice variables={testMissing} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              {t('common:close')}
            </Button>
            <Button onClick={handleTestSend} disabled={!testEmail || testSending}>
              {testSending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {t('step.sendTestButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
